import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ScorecardNavigation from './ScorecardNavigation';
import { TrendingUp, TrendingDown, Minus, Users, Award, ChevronDown, Activity, Search, Globe, Trophy, Target } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// ─── Types ───────────────────────────────────────────────────────────────────

type Region = 'us' | 'sg';

interface Server {
  id: string;
  firstName: string;
  lastName: string;
  restaurant: string;
  city: string;
  state: string;
  category: 'Quick Service' | 'Fast Casual' | 'Casual Dining' | 'Fine Dining';
  score: number;
  upsellRate: number;
  successRate: number;
  interactions: number;
  rankChange: number;
  greeting: number;
  region: Region;
}

interface CityMarker {
  name: string;
  abbr: string;
  lat: number;
  lng: number;
}

interface LiveEvent {
  type: 'upsell_success' | 'upsell_attempt' | 'greeting' | 'rank_up';
  server: string;
  item?: string;
  restaurant: string;
  city: string;
  newRank?: number;
  region: Region;
}

// ─── Mock Data ───────────────────────────────────────────────────────────────

const US_SERVERS: Server[] = [
  { id: 'sc01', firstName: 'Sarah', lastName: 'Chen', restaurant: 'SAJJ Mediterranean', city: 'San Francisco', state: 'CA', category: 'Fast Casual', score: 831, upsellRate: 87, successRate: 42, interactions: 1243, rankChange: 2, greeting: 96, region: 'us' },
  { id: 'jl01', firstName: 'Jamie', lastName: 'Liu', restaurant: 'Lazy Bear', city: 'San Francisco', state: 'CA', category: 'Fine Dining', score: 812, upsellRate: 91, successRate: 38, interactions: 876, rankChange: 0, greeting: 98, region: 'us' },
  { id: 'mp01', firstName: 'Miguel', lastName: 'Perez', restaurant: 'Tacolicious', city: 'San Francisco', state: 'CA', category: 'Fast Casual', score: 778, upsellRate: 79, successRate: 35, interactions: 934, rankChange: -1, greeting: 92, region: 'us' },
  { id: 'mw01', firstName: 'Marcus', lastName: 'Williams', restaurant: 'Gen Korean BBQ', city: 'Los Angeles', state: 'CA', category: 'Casual Dining', score: 818, upsellRate: 84, successRate: 40, interactions: 1087, rankChange: 1, greeting: 95, region: 'us' },
  { id: 'ap01', firstName: 'Alex', lastName: 'Park', restaurant: "Canter's Deli", city: 'Los Angeles', state: 'CA', category: 'Casual Dining', score: 795, upsellRate: 81, successRate: 37, interactions: 1156, rankChange: 3, greeting: 93, region: 'us' },
  { id: 'ro01', firstName: 'Rachel', lastName: 'Ortiz', restaurant: 'Sweetgreen', city: 'Los Angeles', state: 'CA', category: 'Fast Casual', score: 762, upsellRate: 76, successRate: 33, interactions: 892, rankChange: -2, greeting: 90, region: 'us' },
  { id: 'em01', firstName: 'Emma', lastName: 'Martinez', restaurant: 'Le Bernardin', city: 'New York', state: 'NY', category: 'Fine Dining', score: 842, upsellRate: 93, successRate: 45, interactions: 654, rankChange: 1, greeting: 99, region: 'us' },
  { id: 'dj01', firstName: 'David', lastName: 'Johnson', restaurant: 'Shake Shack', city: 'New York', state: 'NY', category: 'Quick Service', score: 806, upsellRate: 83, successRate: 39, interactions: 2156, rankChange: 0, greeting: 94, region: 'us' },
  { id: 'tk01', firstName: 'Tyler', lastName: 'Kim', restaurant: 'Sweetgreen', city: 'New York', state: 'NY', category: 'Fast Casual', score: 754, upsellRate: 74, successRate: 31, interactions: 1432, rankChange: -3, greeting: 88, region: 'us' },
  { id: 'jb01', firstName: 'Jake', lastName: 'Bennett', restaurant: 'Alinea', city: 'Chicago', state: 'IL', category: 'Fine Dining', score: 825, upsellRate: 90, successRate: 43, interactions: 432, rankChange: 0, greeting: 97, region: 'us' },
  { id: 'aw01', firstName: 'Aisha', lastName: 'Washington', restaurant: "Portillo's", city: 'Chicago', state: 'IL', category: 'Quick Service', score: 789, upsellRate: 80, successRate: 36, interactions: 1876, rankChange: 2, greeting: 91, region: 'us' },
  { id: 'ls01', firstName: 'Lisa', lastName: 'Santos', restaurant: 'Chipotle', city: 'Chicago', state: 'IL', category: 'Fast Casual', score: 741, upsellRate: 72, successRate: 30, interactions: 2234, rankChange: 1, greeting: 87, region: 'us' },
  { id: 'kb01', firstName: 'Kayla', lastName: 'Brown', restaurant: 'Chick-fil-A', city: 'Austin', state: 'TX', category: 'Quick Service', score: 798, upsellRate: 82, successRate: 38, interactions: 2567, rankChange: 4, greeting: 95, region: 'us' },
  { id: 'rn01', firstName: 'Ryan', lastName: 'Nguyen', restaurant: 'Pappadeaux', city: 'Dallas', state: 'TX', category: 'Casual Dining', score: 773, upsellRate: 78, successRate: 34, interactions: 1098, rankChange: -1, greeting: 91, region: 'us' },
  { id: 'cm01', firstName: 'Chris', lastName: 'Miller', restaurant: 'Canlis', city: 'Seattle', state: 'WA', category: 'Fine Dining', score: 808, upsellRate: 88, successRate: 41, interactions: 543, rankChange: 0, greeting: 96, region: 'us' },
  { id: 'ng01', firstName: 'Nina', lastName: 'Garcia', restaurant: 'Versailles', city: 'Miami', state: 'FL', category: 'Casual Dining', score: 767, upsellRate: 77, successRate: 33, interactions: 987, rankChange: -2, greeting: 89, region: 'us' },
  { id: 'jt01', firstName: 'Jordan', lastName: 'Taylor', restaurant: 'In-N-Out Burger', city: 'Denver', state: 'CO', category: 'Quick Service', score: 783, upsellRate: 79, successRate: 35, interactions: 1654, rankChange: 1, greeting: 92, region: 'us' },
  { id: 'sr01', firstName: 'Sofia', lastName: 'Rodriguez', restaurant: 'Husk', city: 'Nashville', state: 'TN', category: 'Casual Dining', score: 791, upsellRate: 81, successRate: 37, interactions: 876, rankChange: 2, greeting: 93, region: 'us' },
  { id: 'at01', firstName: 'Andre', lastName: 'Thompson', restaurant: 'Legal Sea Foods', city: 'Boston', state: 'MA', category: 'Casual Dining', score: 776, upsellRate: 78, successRate: 34, interactions: 1123, rankChange: 0, greeting: 91, region: 'us' },
  { id: 'hw01', firstName: 'Hannah', lastName: 'Wilson', restaurant: 'The Capital Grille', city: 'Portland', state: 'OR', category: 'Fine Dining', score: 815, upsellRate: 89, successRate: 40, interactions: 567, rankChange: 1, greeting: 97, region: 'us' },
];

const SG_SERVERS: Server[] = [
  { id: 'ms01', firstName: 'Mei Ling', lastName: 'Sim', restaurant: 'Les Amis', city: 'Orchard Road', state: 'SG', category: 'Fine Dining', score: 845, upsellRate: 94, successRate: 46, interactions: 498, rankChange: 1, greeting: 99, region: 'sg' },
  { id: 'wt01', firstName: 'Wei Lin', lastName: 'Tan', restaurant: 'Burnt Ends', city: 'Chinatown', state: 'SG', category: 'Fine Dining', score: 836, upsellRate: 91, successRate: 44, interactions: 412, rankChange: 0, greeting: 98, region: 'sg' },
  { id: 'ah01', firstName: 'Ahmad', lastName: 'Hassan', restaurant: 'Hawker Chan', city: 'Chinatown', state: 'SG', category: 'Quick Service', score: 811, upsellRate: 85, successRate: 41, interactions: 3247, rankChange: 2, greeting: 94, region: 'sg' },
  { id: 'ps01', firstName: 'Priya', lastName: 'Sharma', restaurant: 'Jumbo Seafood', city: 'Clarke Quay', state: 'SG', category: 'Casual Dining', score: 802, upsellRate: 83, successRate: 39, interactions: 876, rankChange: 1, greeting: 95, region: 'sg' },
  { id: 'lk01', firstName: 'Li Wei', lastName: 'Koh', restaurant: 'Din Tai Fung', city: 'Orchard Road', state: 'SG', category: 'Fast Casual', score: 794, upsellRate: 80, successRate: 37, interactions: 1543, rankChange: -1, greeting: 92, region: 'sg' },
  { id: 'sy01', firstName: 'Siti', lastName: 'Yusof', restaurant: 'Lau Pa Sat', city: 'Marina Bay', state: 'SG', category: 'Quick Service', score: 786, upsellRate: 79, successRate: 36, interactions: 2876, rankChange: 3, greeting: 91, region: 'sg' },
  { id: 'rk01', firstName: 'Raj', lastName: 'Kumar', restaurant: 'Violet Oon', city: 'Marina Bay', state: 'SG', category: 'Casual Dining', score: 779, upsellRate: 78, successRate: 35, interactions: 934, rankChange: 0, greeting: 90, region: 'sg' },
  { id: 'ct01', firstName: 'Chen', lastName: 'Ting', restaurant: 'PS.Cafe', city: 'Holland Village', state: 'SG', category: 'Casual Dining', score: 768, upsellRate: 76, successRate: 33, interactions: 756, rankChange: -2, greeting: 89, region: 'sg' },
  { id: 'dw01', firstName: 'Daniel', lastName: 'Wong', restaurant: 'Crystal Jade', city: 'Bugis', state: 'SG', category: 'Fast Casual', score: 758, upsellRate: 75, successRate: 32, interactions: 1234, rankChange: 1, greeting: 88, region: 'sg' },
  { id: 'na01', firstName: 'Nurul', lastName: 'Aisyah', restaurant: 'Tanjong Beach Club', city: 'Sentosa', state: 'SG', category: 'Casual Dining', score: 747, upsellRate: 73, successRate: 31, interactions: 654, rankChange: -1, greeting: 87, region: 'sg' },
];

const ALL_SERVERS = [...US_SERVERS, ...SG_SERVERS];

const US_CITIES: CityMarker[] = [
  { name: 'Seattle', abbr: 'SEA', lat: 47.6062, lng: -122.3321 },
  { name: 'Portland', abbr: 'PDX', lat: 45.5155, lng: -122.6789 },
  { name: 'San Francisco', abbr: 'SF', lat: 37.7749, lng: -122.4194 },
  { name: 'Los Angeles', abbr: 'LA', lat: 34.0522, lng: -118.2437 },
  { name: 'Denver', abbr: 'DEN', lat: 39.7392, lng: -104.9903 },
  { name: 'Austin', abbr: 'ATX', lat: 30.2672, lng: -97.7431 },
  { name: 'Dallas', abbr: 'DFW', lat: 32.7767, lng: -96.7970 },
  { name: 'Chicago', abbr: 'CHI', lat: 41.8781, lng: -87.6298 },
  { name: 'Nashville', abbr: 'NSH', lat: 36.1627, lng: -86.7816 },
  { name: 'Miami', abbr: 'MIA', lat: 25.7617, lng: -80.1918 },
  { name: 'New York', abbr: 'NYC', lat: 40.7128, lng: -74.0060 },
  { name: 'Boston', abbr: 'BOS', lat: 42.3601, lng: -71.0589 },
];

const SG_CITIES: CityMarker[] = [
  { name: 'Orchard Road', abbr: 'ORC', lat: 1.3048, lng: 103.8318 },
  { name: 'Marina Bay', abbr: 'MBS', lat: 1.2814, lng: 103.8586 },
  { name: 'Chinatown', abbr: 'CHN', lat: 1.2833, lng: 103.8433 },
  { name: 'Clarke Quay', abbr: 'CLQ', lat: 1.2905, lng: 103.8466 },
  { name: 'Holland Village', abbr: 'HLV', lat: 1.3115, lng: 103.7959 },
  { name: 'Bugis', abbr: 'BGS', lat: 1.3009, lng: 103.8558 },
  { name: 'Sentosa', abbr: 'SEN', lat: 1.2494, lng: 103.8303 },
];

const REGION_CONFIG: Record<Region, { center: [number, number]; zoom: number; label: string; totalServers: number; avgScore: number }> = {
  us: { center: [39.8, -98.5], zoom: 4, label: 'United States', totalServers: 12847, avgScore: 687 },
  sg: { center: [1.35, 103.82], zoom: 12, label: 'Singapore', totalServers: 3241, avgScore: 712 },
};

const LIVE_EVENTS: LiveEvent[] = [
  { type: 'upsell_success', server: 'Sarah C.', item: 'Sparkling Water', restaurant: 'SAJJ Mediterranean', city: 'SF', region: 'us' },
  { type: 'upsell_attempt', server: 'Marcus W.', item: 'Bulgogi Bowl Upgrade', restaurant: 'Gen Korean BBQ', city: 'LA', region: 'us' },
  { type: 'upsell_success', server: 'Mei Ling S.', item: 'Wine Pairing', restaurant: 'Les Amis', city: 'ORC', region: 'sg' },
  { type: 'upsell_success', server: 'Emma M.', item: 'Wine Pairing', restaurant: 'Le Bernardin', city: 'NYC', region: 'us' },
  { type: 'greeting', server: 'Jake B.', item: 'Warm Welcome', restaurant: 'Alinea', city: 'CHI', region: 'us' },
  { type: 'upsell_success', server: 'Ahmad H.', item: 'Soya Sauce Chicken', restaurant: 'Hawker Chan', city: 'CHN', region: 'sg' },
  { type: 'upsell_success', server: 'Kayla B.', item: 'Large Milkshake', restaurant: 'Chick-fil-A', city: 'ATX', region: 'us' },
  { type: 'rank_up', server: 'Kayla B.', restaurant: 'Chick-fil-A', city: 'ATX', newRank: 5, region: 'us' },
  { type: 'upsell_attempt', server: 'Li Wei K.', item: 'Truffle Dumpling', restaurant: 'Din Tai Fung', city: 'ORC', region: 'sg' },
  { type: 'upsell_attempt', server: 'David J.', item: 'ShackBurger Double', restaurant: 'Shake Shack', city: 'NYC', region: 'us' },
  { type: 'upsell_success', server: 'Priya S.', item: 'Chilli Crab Add-on', restaurant: 'Jumbo Seafood', city: 'CLQ', region: 'sg' },
  { type: 'upsell_success', server: 'Chris M.', item: 'Truffle Supplement', restaurant: 'Canlis', city: 'SEA', region: 'us' },
  { type: 'greeting', server: 'Siti Y.', item: 'Table Greeting', restaurant: 'Lau Pa Sat', city: 'MBS', region: 'sg' },
  { type: 'greeting', server: 'Nina G.', item: 'Table Greeting', restaurant: 'Versailles', city: 'MIA', region: 'us' },
  { type: 'upsell_success', server: 'Sofia R.', item: 'Craft Cocktail', restaurant: 'Husk', city: 'NSH', region: 'us' },
  { type: 'rank_up', server: 'Ahmad H.', restaurant: 'Hawker Chan', city: 'CHN', newRank: 3, region: 'sg' },
  { type: 'rank_up', server: 'Sarah C.', restaurant: 'SAJJ Mediterranean', city: 'SF', newRank: 1, region: 'us' },
  { type: 'upsell_attempt', server: 'Jordan T.', item: 'Animal Style Fries', restaurant: 'In-N-Out Burger', city: 'DEN', region: 'us' },
  { type: 'upsell_success', server: 'Raj K.', item: 'Laksa Upgrade', restaurant: 'Violet Oon', city: 'MBS', region: 'sg' },
  { type: 'upsell_success', server: 'Andre T.', item: 'Lobster Bisque', restaurant: 'Legal Sea Foods', city: 'BOS', region: 'us' },
  { type: 'upsell_success', server: 'Chen T.', item: 'Truffle Fries', restaurant: 'PS.Cafe', city: 'HLV', region: 'sg' },
  { type: 'upsell_success', server: 'Lisa S.', item: 'Guac & Chips', restaurant: 'Chipotle', city: 'CHI', region: 'us' },
  { type: 'greeting', server: 'Hannah W.', item: 'VIP Greeting', restaurant: 'The Capital Grille', city: 'PDX', region: 'us' },
  { type: 'upsell_success', server: 'Daniel W.', item: 'XO Fried Rice', restaurant: 'Crystal Jade', city: 'BGS', region: 'sg' },
];

const CATEGORY_STYLES: Record<string, string> = {
  'Quick Service': 'bg-orange-100 text-orange-700',
  'Fast Casual': 'bg-blue-100 text-blue-700',
  'Casual Dining': 'bg-emerald-100 text-emerald-700',
  'Fine Dining': 'bg-purple-100 text-purple-700',
};

const CATEGORIES = ['All', 'Quick Service', 'Fast Casual', 'Casual Dining', 'Fine Dining'];

// ─── Avatar helper ───────────────────────────────────────────────────────────

const getAvatarUrl = (firstName: string, lastName: string, size = 80) =>
  `https://api.dicebear.com/9.x/avataaars/svg?seed=${firstName}${lastName}&size=${size}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf`;

// ─── Map helpers ─────────────────────────────────────────────────────────────

const createCityIcon = (abbr: string, count: number, isActive: boolean) => {
  const dotColor = count > 0 ? '#34d399' : '#9ca3af';
  const glow = count > 0 ? '0 0 12px rgba(52,211,153,0.7), 0 0 4px rgba(52,211,153,0.9)' : 'none';
  const labelColor = isActive ? '#6ee7b7' : '#d1d5db';

  return L.divIcon({
    html: `
      <div style="display:flex;flex-direction:column;align-items:center;cursor:pointer;">
        <div style="width:12px;height:12px;border-radius:50%;background:${dotColor};box-shadow:${glow};border:1.5px solid rgba(255,255,255,0.3);"></div>
        <div style="color:${labelColor};font-size:11px;font-weight:700;margin-top:4px;white-space:nowrap;text-shadow:0 1px 4px rgba(0,0,0,1), 0 0 8px rgba(0,0,0,0.8);">
          ${abbr}${count > 0 ? ` (${count})` : ''}
        </div>
      </div>
    `,
    className: '',
    iconSize: [80, 36],
    iconAnchor: [40, 6],
  });
};

const MapController: React.FC<{ cities: CityMarker[] }> = ({ cities }) => {
  const map = useMap();
  useEffect(() => {
    if (cities.length === 0) return;
    const bounds = L.latLngBounds(cities.map(c => [c.lat, c.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [40, 40], animate: false });
  }, [cities, map]);
  return null;
};

// ─── Helper Components ───────────────────────────────────────────────────────

const ScoreGauge: React.FC<{ score: number; size?: number }> = ({ score, size = 48 }) => {
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const normalized = Math.max(0, Math.min(1, (score - 300) / 550));
  const offset = circumference * (1 - normalized);
  const color = score >= 750 ? '#10b981' : score >= 650 ? '#6366f1' : score >= 550 ? '#f59e0b' : '#ef4444';

  return (
    <svg width={size} height={size}>
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
        strokeDasharray={circumference} strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
        style={{ fontSize: size * 0.3, fontWeight: 700, fill: '#111827' }}>
        {score}
      </text>
    </svg>
  );
};

const RankChangeIndicator: React.FC<{ change: number }> = ({ change }) => {
  if (change > 0) return (
    <span className="inline-flex items-center gap-0.5 text-green-600 text-xs font-medium">
      <TrendingUp className="w-3 h-3" /> +{change}
    </span>
  );
  if (change < 0) return (
    <span className="inline-flex items-center gap-0.5 text-red-500 text-xs font-medium">
      <TrendingDown className="w-3 h-3" /> {change}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 text-gray-400 text-xs font-medium">
      <Minus className="w-3 h-3" /> 0
    </span>
  );
};

const LiveEventRow: React.FC<{ event: LiveEvent; isNew?: boolean }> = ({ event, isNew }) => {
  const dotColor = event.type === 'upsell_success' ? 'bg-green-400'
    : event.type === 'upsell_attempt' ? 'bg-yellow-400'
    : event.type === 'rank_up' ? 'bg-purple-400'
    : 'bg-blue-400';

  const label = event.type === 'upsell_success' ? `${event.server} upsold ${event.item}`
    : event.type === 'upsell_attempt' ? `${event.server} offered ${event.item}`
    : event.type === 'rank_up' ? `${event.server} moved to #${event.newRank}`
    : `${event.server} greeted a guest`;

  return (
    <div className={`flex items-start gap-2 py-1.5 px-2 rounded transition-all duration-500 ${isNew ? 'bg-gray-700/50' : ''}`}>
      <span className={`w-2 h-2 rounded-full ${dotColor} mt-1.5 flex-shrink-0`} />
      <div className="min-w-0">
        <p className="text-gray-200 text-xs truncate">{label}</p>
        <p className="text-gray-500 text-[10px]">{event.restaurant} &middot; {event.city}</p>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const BespokeScore: React.FC = () => {
  const navigate = useNavigate();
  const [region, setRegion] = useState<Region>('us');
  const [geoFilter, setGeoFilter] = useState('All');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [geoOpen, setGeoOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [feedIndex, setFeedIndex] = useState(0);

  const regionConfig = REGION_CONFIG[region];
  const currentCities = region === 'us' ? US_CITIES : SG_CITIES;
  const currentServers = ALL_SERVERS.filter(s => s.region === region);
  const geoOptions = ['All', ...currentCities.map(c => c.name)];

  // Reset geo filter when region changes
  useEffect(() => {
    setGeoFilter('All');
  }, [region]);

  // Live feed cycling — filter to current region
  const regionEvents = useMemo(() => LIVE_EVENTS.filter(e => e.region === region), [region]);
  useEffect(() => {
    setFeedIndex(0);
    const interval = setInterval(() => {
      setFeedIndex(prev => (prev + 1) % regionEvents.length);
    }, 2200);
    return () => clearInterval(interval);
  }, [regionEvents.length]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = () => { setGeoOpen(false); setCatOpen(false); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Filter & sort servers
  const filteredServers = currentServers
    .filter(s => geoFilter === 'All' || s.city === geoFilter)
    .filter(s => categoryFilter === 'All' || s.category === categoryFilter)
    .filter(s => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return `${s.firstName} ${s.lastName}`.toLowerCase().includes(q) || s.restaurant.toLowerCase().includes(q);
    })
    .sort((a, b) => b.score - a.score);

  // Visible live events
  const visibleEvents = regionEvents.length > 0
    ? Array.from({ length: Math.min(8, regionEvents.length) }, (_, i) => regionEvents[(feedIndex + i) % regionEvents.length])
    : [];

  const topPerformer = filteredServers[0];

  const getScoreLabel = (score: number) => {
    if (score >= 800) return 'Exceptional';
    if (score >= 750) return 'Excellent';
    if (score >= 700) return 'Very Good';
    if (score >= 650) return 'Good';
    if (score >= 600) return 'Fair';
    return 'Developing';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ScorecardNavigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ─── Header ───────────────────────────────────────────────── */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                BespokeScore<span className="text-indigo-600 text-lg align-super">&trade;</span>
              </h1>
              <p className="text-sm text-gray-500">The standardized hospitality performance metric</p>
            </div>
          </div>
        </div>

        {/* ─── Filters ──────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          {/* Region toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setRegion('us')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${region === 'us' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              United States
            </button>
            <button
              onClick={() => setRegion('sg')}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${region === 'sg' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Singapore
            </button>
          </div>

          {/* Geography */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setGeoOpen(!geoOpen); setCatOpen(false); }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <Globe className="w-4 h-4 text-gray-400" />
              {geoFilter === 'All' ? `All ${regionConfig.label}` : geoFilter}
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
            {geoOpen && (
              <div className="absolute z-20 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 max-h-64 overflow-y-auto">
                {geoOptions.map(city => (
                  <button key={city} onClick={() => { setGeoFilter(city); setGeoOpen(false); }}
                    className={`block w-full text-left px-4 py-2 text-sm ${geoFilter === city ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}>
                    {city === 'All' ? `All ${regionConfig.label}` : city}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Category */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setCatOpen(!catOpen); setGeoOpen(false); }}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <Target className="w-4 h-4 text-gray-400" />
              {categoryFilter === 'All' ? 'All Categories' : categoryFilter}
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
            {catOpen && (
              <div className="absolute z-20 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1">
                {CATEGORIES.map(cat => (
                  <button key={cat} onClick={() => { setCategoryFilter(cat); setCatOpen(false); }}
                    className={`block w-full text-left px-4 py-2 text-sm ${categoryFilter === cat ? 'bg-indigo-100 text-indigo-700 font-medium' : 'text-gray-700 hover:bg-gray-100'}`}>
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or restaurant..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* ─── Stats Cards ──────────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Servers Scored</p>
                <p className="text-2xl font-bold text-gray-900">{regionConfig.totalServers.toLocaleString()}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {region === 'us' ? `Across ${US_CITIES.length} cities nationwide` : `Across ${SG_CITIES.length} districts in Singapore`}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Activity className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Average BespokeScore</p>
                <p className="text-2xl font-bold text-gray-900">{regionConfig.avgScore}</p>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">Scale: 300 – 850 &middot; {regionConfig.label} average</p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Top Performer</p>
                <p className="text-2xl font-bold text-gray-900">{topPerformer ? `${topPerformer.firstName} ${topPerformer.lastName.charAt(0)}.` : '—'}</p>
              </div>
            </div>
            {topPerformer && (
              <p className="text-xs text-gray-400 mt-2">Score: {topPerformer.score} &middot; {topPerformer.restaurant}</p>
            )}
          </div>
        </div>

        {/* ─── Map + Live Feed ──────────────────────────────────────── */}
        <div className="bg-gray-900 rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-400" />
              Live Activity — {regionConfig.label}
            </h3>
            <span className="text-green-400 text-xs flex items-center gap-1.5">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              Real-time
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" style={{ height: '400px' }}>
            {/* Map */}
            <div className="lg:col-span-2 rounded-lg overflow-hidden relative h-full" style={{ zIndex: 0 }}>
              <style>{`.bespoke-map .leaflet-tile { filter: invert(1) brightness(0.55) contrast(1.3); }`}</style>
              <MapContainer
                className="bespoke-map"
                center={regionConfig.center}
                zoom={regionConfig.zoom}
                zoomControl={false}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://tiles.stadiamaps.com/tiles/stamen_toner_background/{z}/{x}/{y}{r}.png"
                />
                <TileLayer
                  attribution='&copy; <a href="https://stadiamaps.com/">Stadia</a> &copy; <a href="https://stamen.com/">Stamen</a>'
                  url="https://tiles.stadiamaps.com/tiles/stamen_toner_lines/{z}/{x}/{y}{r}.png"
                />
                <MapController cities={currentCities} />
                {currentCities.map(city => {
                  const serverCount = currentServers.filter(s => s.city === city.name).length;
                  const isActive = geoFilter === city.name;
                  return (
                    <Marker
                      key={city.abbr}
                      position={[city.lat, city.lng]}
                      icon={createCityIcon(city.abbr, serverCount, isActive)}
                      eventHandlers={{ click: () => setGeoFilter(geoFilter === city.name ? 'All' : city.name) }}
                    />
                  );
                })}
              </MapContainer>
            </div>

            {/* Live Feed */}
            <div className="flex flex-col h-full overflow-hidden">
              <div className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-2">Activity Feed</div>
              <div className="space-y-0.5 flex-1 overflow-y-auto">
                {visibleEvents.map((event, idx) => (
                  <LiveEventRow key={`${feedIndex}-${idx}`} event={event} isNew={idx === 0} />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ─── Leaderboard Table ─────────────────────────────────────── */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                {geoFilter === 'All' ? regionConfig.label : geoFilter} Rankings
                {categoryFilter !== 'All' && ` — ${categoryFilter}`}
              </h3>
              <span className="text-sm text-gray-500">{filteredServers.length} servers</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Rank</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Server</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Restaurant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Upsell %</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Success %</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Interactions</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Trend</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredServers.map((server, idx) => {
                  const rank = idx + 1;
                  return (
                    <tr
                      key={server.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/restaurant/bespoke-score/${server.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {rank <= 3 ? (
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : 'bg-amber-700'}`}>
                            {rank}
                          </span>
                        ) : (
                          <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium text-gray-500 bg-gray-100">
                            {rank}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900 hover:text-indigo-600">
                          {server.firstName} {server.lastName}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-700">{server.restaurant}</div>
                        <div className="text-xs text-gray-400">{server.city}{server.state !== 'SG' ? `, ${server.state}` : ', Singapore'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${CATEGORY_STYLES[server.category]}`}>
                          {server.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">{server.upsellRate}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-900">{server.successRate}%</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">{server.interactions.toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <RankChangeIndicator change={server.rankChange} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          <ScoreGauge score={server.score} size={40} />
                          <span className="text-[10px] text-gray-400">{getScoreLabel(server.score)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredServers.length === 0 && (
            <div className="px-6 py-12 text-center">
              <p className="text-gray-500 text-sm">No servers found matching your filters.</p>
            </div>
          )}
        </div>

        {/* Scale legend */}
        <div className="mt-4 flex items-center justify-center gap-6 text-xs text-gray-400">
          <span>BespokeScore Scale: 300–850</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> 750+ Excellent</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500" /> 650–749 Good</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> 550–649 Fair</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> &lt;550 Developing</span>
        </div>
      </div>
    </div>
  );
};

export default BespokeScore;
